import { Flex } from "@redotech/arbiter/flex";
import { Icon } from "@redotech/arbiter/icon";
import { Text } from "@redotech/arbiter/text";
import { InteractiveMapLocation } from "@redotech/redo-common-model-location-common/interactive-map-location";
import { usePickupModals } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-pickup-modals";
import {
  StepIdentifierEnum,
  StepType,
} from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-steps";
import { useTranslation } from "@redotech/redo-i18n";
import CheckCircleFilled from "@redotech/redo-icons/check-circle_filled.svg";
import PlaceHolder from "@redotech/redo-icons/placeholder_filled.svg";
import { Resource } from "@redotech/redo-team-common/localization/resource";
import { assertNever } from "@redotech/util/type";
import { ReactElement } from "react";
import { BlueYonderLocationsSearchParams } from "./blue-yonder-locations-button";
import { DropoffConfirmation } from "./dropoffs/dropoff-confirmation";
import { DropoffLocationsSearchParams } from "./dropoffs/dropoff-locations-button";
import {
  InStoreLocationsButton,
  InStoreLocationsSearchParams,
} from "./in-store-locations";
import { InStorePackingSlipButton } from "./in-store-packing-slip-button";
import { LabelButtons } from "./label-buttons";
import { PickupButtons } from "./pickup-buttons";
import {
  ReturnLocationsButton,
  ReturnLocationsSearchParams,
} from "./return-locations-button";

export interface ReturnStepsSearchCallbacks {
  searchCarrierLocations: (
    params: ReturnLocationsSearchParams,
  ) => Promise<InteractiveMapLocation[]>;
  searchInStoreLocations: (
    params: InStoreLocationsSearchParams,
  ) => Promise<InteractiveMapLocation[]>;
  searchBlueYonderLocations: (
    params: BlueYonderLocationsSearchParams,
  ) => Promise<InteractiveMapLocation[]>;
  searchDropoffLocations: (
    params: DropoffLocationsSearchParams,
  ) => Promise<InteractiveMapLocation[]>;
}

interface IReturnStepsCardProps extends ReturnStepsSearchCallbacks {
  returnId: string;
  copyResource: Resource | undefined;
  shouldAutoOpenBoxlessLocations?: boolean;
  steps: StepType[] | undefined;
  pickupModals: ReturnType<typeof usePickupModals>;
  onOpenUrl: (url: string) => void;
}

export function ReturnStepsCard({
  returnId,
  copyResource,
  shouldAutoOpenBoxlessLocations = false,
  steps,
  pickupModals,
  searchCarrierLocations,
  searchInStoreLocations,
  searchBlueYonderLocations,
  searchDropoffLocations,
  onOpenUrl,
}: IReturnStepsCardProps) {
  const { t } = useTranslation();
  const stepsTitle = copyResource?.STATUS_STEPS_TITLE;

  function getStepComponent(step: StepType): ReactElement | null {
    switch (step.type) {
      case StepIdentifierEnum.DROP_OFF_PACKAGE:
        return (
          <ReturnLocationsButton
            searchCarrierLocations={searchCarrierLocations}
            trackable={step.trackable}
          />
        );
      case StepIdentifierEnum.DROPOFF:
        return (
          <DropoffConfirmation
            autoOpen={shouldAutoOpenBoxlessLocations}
            customerCountry={step.customerCountry}
            dropoffs={step.dropoffs}
            modalSubtitle={step.modalSubtitle}
            modalTitle={step.modalTitle}
            onOpenUrl={onOpenUrl}
            returnId={returnId}
            searchDropoffLocations={searchDropoffLocations}
          />
        );
      case StepIdentifierEnum.IN_STORE_PRINT_PACKING_SLIP:
        return (
          <InStorePackingSlipButton
            onOpenUrl={onOpenUrl}
            packingSlipLink={step.packingSlipLink}
          />
        );
      case StepIdentifierEnum.IN_STORE_RETURN:
        return (
          <InStoreLocationsButton
            returnId={returnId}
            searchInStoreLocations={searchInStoreLocations}
          />
        );
      case StepIdentifierEnum.PICKUP_PRINT_LABEL:
      case StepIdentifierEnum.SHIPMENT_TRANSIT:
        return step.shipmentInfo ? (
          <LabelButtons
            carrier={step.shipmentInfo.carrier}
            commercialInvoice={step.shipmentInfo.commercialInvoice}
            onOpenUrl={onOpenUrl}
            qrCodeOnly={step.shipmentInfo.qrCodeOnly}
            trackable={step.shipmentInfo.trackable}
          />
        ) : null;
      case StepIdentifierEnum.PICKUP_MANAGE:
        return <PickupButtons pickupModals={pickupModals} />;
      case StepIdentifierEnum.COLLECTION_SCHEDULED:
      case StepIdentifierEnum.EXCHANGE_ORDER:
      case StepIdentifierEnum.GREEN_RETURN_INSTRUCTIONS:
      case StepIdentifierEnum.GREEN_RETURN_PROCESSED:
      case StepIdentifierEnum.HAPPY_RETURNS_DROP_OFF:
      case StepIdentifierEnum.HAPPY_RETURNS_PROCESSED:
      case StepIdentifierEnum.IN_STORE_PROCESSED:
      case StepIdentifierEnum.MANUAL_REVIEW:
      case StepIdentifierEnum.NONE:
      case StepIdentifierEnum.PENDING_LABEL:
      case StepIdentifierEnum.PICKUP_ATTACH_LABEL:
      case StepIdentifierEnum.PROCESSED:
      case StepIdentifierEnum.PROCESSED_REPAIR: //TODO: TrackRepairButton
      case StepIdentifierEnum.SHIP_BACK_REPAIR:
      case StepIdentifierEnum.WARNING:
        return null;
      default:
        assertNever(step);
    }
  }
  return (
    <Flex
      data-ui="return-steps"
      flexDirection="column"
      gap="3xl"
      pb="sm"
      pt="sm"
    >
      <Text as="h2" fontSize="lg" fontWeight="semibold">
        {stepsTitle === "What happens next?"
          ? t("What happens next?")
          : (stepsTitle ?? t("What happens next?"))}
      </Text>
      <Flex data-ui="steps-list" flexDirection="column">
        {steps?.map((step: StepType, i) => {
          return (
            <Flex data-ui="step-item" flexDirection="row" gap="sm" key={i}>
              <Flex align="center" flexDirection="column">
                <Flex flexDirection="column">
                  <Icon
                    arbiterIconSvg={(props) =>
                      step.complete
                        ? CheckCircleFilled(props)
                        : PlaceHolder(props)
                    }
                    color={step.complete ? "success" : "light"}
                    size="compact"
                  />
                </Flex>
                {i != steps.length - 1 && (
                  <Flex
                    flex={1}
                    flexDirection="column"
                    pb="xxs"
                    pt="xxs"
                    style={{
                      borderColor: "var(--redo-colors-border-border-secondary)",
                      borderWidth: 1,
                      borderRadius: "var(--border-radius-2xl)",
                      borderStyle: "solid",
                      width: "2px",
                    }}
                  />
                )}
              </Flex>

              <Flex flex={1} flexDirection="column" pb="sm">
                <Text fontSize="md" fontWeight="medium">
                  {step.title}
                </Text>
                <Text fontSize="sm" fontWeight="regular" textColor="secondary">
                  {step.subtitle}
                </Text>
                {getStepComponent(step)}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}
